import { useState, useCallback, useRef } from 'react'

interface SwipeConfig {
  threshold?: number
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  onSwipeUp?: () => void
  onSwipeDown?: () => void
}

export function useSwipeActions({
  threshold = 50,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown
}: SwipeConfig) {
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const [currentPosition, setCurrentPosition] = useState({ x: 0, y: 0 })
  const [isSwiping, setIsSwiping] = useState(false)
  const swipeRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = useCallback((e: TouchEvent | React.TouchEvent) => {
    const touch = 'touches' in e ? e.touches[0] : e
    setStartPosition({ x: touch.clientX, y: touch.clientY })
    setCurrentPosition({ x: touch.clientX, y: touch.clientY })
    setIsSwiping(true)
  }, [])

  const handleTouchMove = useCallback((e: TouchEvent | React.TouchEvent) => {
    if (!isSwiping) return
    
    const touch = 'touches' in e ? e.touches[0] : e
    setCurrentPosition({ x: touch.clientX, y: touch.clientY })
  }, [isSwiping])

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping) return
    
    const deltaX = currentPosition.x - startPosition.x
    const deltaY = currentPosition.y - startPosition.y
    
    // Determine if this is primarily a horizontal or vertical swipe
    const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY)
    
    if (isHorizontal) {
      if (deltaX > threshold && onSwipeRight) {
        onSwipeRight()
      } else if (deltaX < -threshold && onSwipeLeft) {
        onSwipeLeft()
      }
    } else {
      if (deltaY > threshold && onSwipeDown) {
        onSwipeDown()
      } else if (deltaY < -threshold && onSwipeUp) {
        onSwipeUp()
      }
    }
    
    setIsSwiping(false)
    setStartPosition({ x: 0, y: 0 })
    setCurrentPosition({ x: 0, y: 0 })
  }, [isSwiping, currentPosition, startPosition, threshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown])

  const swipeProgress = {
    x: isSwiping ? currentPosition.x - startPosition.x : 0,
    y: isSwiping ? currentPosition.y - startPosition.y : 0
  }

  const bind = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    ref: swipeRef
  }

  return {
    bind,
    isSwiping,
    swipeProgress,
    swipeRef
  }
}